import React, { useContext } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import ProfileLoading from "./ProfileLoading";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * SUBSCRIPTIONS ESCALATION CLASS. Styling-only: no handler, condition, state
 * value, prop or DOM node moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer` and `myAccountTitle` were declared but never
 * referenced from the JSX (copy-paste from MyAccount.js), so they rendered
 * nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>, which takes a
// `className` but has no `sx` prop (guide §1.2).
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

// was: useStyles().bodyList — a bare <ul>, which has no `sx` prop.
const BodyList = styled("ul")({
  fontSize: "1rem",
  fontFamily: "Roboto, Helvetica Neue ,Arial,sans-serif",
  fontWeight: "400",
  lineHeight: "1.2rem",
  "& span": {
    fontWeight: 500,
  },
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().bodyTextSml
const bodyTextSmlSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().formContainer
const formContainerSx = (theme) => ({
  // plus v4's `.MuiGrid-container` width (see v4GridContainerWidthSx above)
  width: "100%",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
});

// was: useStyles().button, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default, which would paint the label lime on the lime
// `typography.button` background — an invisible "Yes, Turn Off Auto Renew"
// button. The `variant="outlined"` sibling needs it too (themeV5's `outlined`
// slot only restates `color` on `:hover`) — same call as `Overview.js`.
const buttonSx = (theme) => ({
  marginTop: theme.spacing(2),
  width: "100%",
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
  color: "text.primary",
});

export default function AutoRenewOff() {
  const { addToast } = useToasts();

  // *** To handle future dates ***

  // Account Context
  const { handleCancelMembership, userProfile, updating, validateLogin } =
    useContext(AccountContext);

  const handleTurnOffAutoRenew = async () => {
    try {
      // Await the result so the success toast only fires once the cancel
      // actually resolves; a rejection lands in catch instead of being
      // swallowed as an unhandled promise (#567).
      await handleCancelMembership();
      addToast("Auto Renew Disabled", {
        appearance: "success",
      });
      validateLogin();
    } catch (e) {
      addToast(e.toString(), {
        appearance: "error",
      });
    }
  };

  // #760 — `userProfile` is undefined until AccountContext resolves the
  // profile (AccountContext.tsx:69/:170), so the `userProfile.productAutoRenewing`
  // reads below threw on every cold navigation to this route and the
  // fallback-less error boundary blanked the page (#774). Safe as an early
  // return because it sits AFTER every hook in this component — the hook order
  // is identical on the loading and loaded renders. Same guard as
  // ChangeName.js:212, with a loading state instead of rendering nothing.
  if (!userProfile) {
    return <ProfileLoading />;
  }

  return (
    <Grid container sx={v4GridContainerWidthSx}>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
          <ActiveBreadcrumbLink to="/myaccount/overview">
            Overview
          </ActiveBreadcrumbLink>
          <Typography sx={disabledBreadcrumbSx}>Turn Off Auto Renew</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Typography variant="h2" sx={myAccountSubTitleSx}>
          Turn Off Auto Renew
        </Typography>
      </Grid>
      <Grid container sx={formContainerSx}>
        {!updating && (
          <>
            {userProfile.productAutoRenewing && (
              <>
                <Grid size={{ xs: 12, sm: 10, md: 10 }}>
                  <Typography variant="body1" sx={bodyTextSx}>
                    By turning off Auto Renew, you will:
                    <br />
                  </Typography>
                  <BodyList>
                    <li>
                      still have access to FarmFLiX for the remainder of your
                      billing period, to{" "}
                      <span>
                        {userProfile.subscriptionExpiryDate &&
                          new Intl.DateTimeFormat("en-GB", {
                            year: "numeric",
                            month: "long",
                            day: "2-digit",
                          }).format(
                            new Date(userProfile.subscriptionExpiryDate),
                          )}
                      </span>
                      . After this period, you will no longer have access to
                      FarmFLiX content.
                    </li>
                    <li>
                      no longer be charged after your billing period has
                      elapsed.
                    </li>
                  </BodyList>
                  <br />
                  <Typography variant="body1" sx={bodyTextSx}>
                    <span>
                      Are you sure you wish to turn off Auto Renew on your
                      FarmFLiX Membership?
                    </span>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Button onClick={handleTurnOffAutoRenew} sx={buttonSx}>
                    Yes, Turn Off Auto Renew
                  </Button>
                  <Link
                    to="/myaccount/overview"
                    style={{ textDecoration: "none" }}
                  >
                    <Button variant="outlined" sx={buttonSx}>
                      No, What Am I Thinking!
                    </Button>
                  </Link>
                </Grid>
              </>
            )}
            {!userProfile.productAutoRenewing && (
              <>
                <Grid size={{ xs: 12, sm: 10, md: 10 }}>
                  <Typography variant="body1" sx={bodyTextSx}>
                    Your Auto Renew has been successfully disabled.
                    <br />
                  </Typography>
                  <Link
                    to="/myaccount/overview"
                    style={{ textDecoration: "none" }}
                  >
                    <Button sx={buttonSx}>Go to Account Overview</Button>
                  </Link>
                </Grid>
              </>
            )}
          </>
        )}
        {updating && (
          <Box sx={{ m: 5 }} align="center">
            <CircularProgress variant="indeterminate"></CircularProgress>
            <Typography variant="body2" sx={bodyTextSmlSx}>
              <br />
              Updating... Please do not refresh your browser.
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
